import { useState, useEffect } from "react";
import { FaCalendarDay, FaClipboardList, FaClock, FaStickyNote } from "react-icons/fa";
import Modal from "../modal/modal";
import DatePicker from "../modal/datepicker";
import TimePicker from "../modal/timepicker";
import "./attendance.css";

const RegularizationModal = ({ attendanceRecord, attendanceList = [], onSave, onClose }) => {
  const [selectedDate, setSelectedDate] = useState(attendanceRecord?.date || "");
  const [form, setForm] = useState({
    requested_check_in: "",
    requested_check_out: "",
    requested_break_hours: "",
    reason: "",
  });

  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const activeRecord = attendanceRecord || (selectedDate ? attendanceList.find(a => a.date === selectedDate) : null) || null;

  // Pre-fill the record-derived fields whenever the active record changes.
  // Uses a functional update and only touches check-in/check-out/break-hours —
  // NOT the whole form — so a user's already-typed "reason" survives if they
  // pick a date, start typing, then change the date (e.g. to correct it).
  useEffect(() => {
    if (activeRecord) {
      setForm((prev) => ({
        ...prev,
        requested_check_in: activeRecord.check_in_time || "",
        requested_check_out: activeRecord.check_out_time || "",
        requested_break_hours: activeRecord.total_break_hours ?? "",
      }));
    } else if (selectedDate) {
      setForm((prev) => ({
        ...prev,
        requested_check_in: "",
        requested_check_out: "",
        requested_break_hours: "",
      }));
    }
  }, [activeRecord, selectedDate]);

  const handleSave = async () => {
    const errs = {};
    if (!activeRecord) {
      errs.attendance = selectedDate ? "No attendance record found on this date. Please choose another date." : "Please select an attendance date from the calendar";
    }
    if (!form.reason) {
      errs.reason = "Reason is required";
    }
    if (form.requested_check_in && form.requested_check_out && form.requested_check_out < form.requested_check_in) {
      errs.requested_check_out = "Check-out time must be after check-in time";
    }
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return setError("Please check the field errors below.");
    }
    setError("");
    setFieldErrors({});
    setSaving(true);
    try {
      await onSave({
        attendance: activeRecord.id,
        requested_check_in: form.requested_check_in ? (form.requested_check_in.length === 5 ? `${form.requested_check_in}:00` : form.requested_check_in) : null,
        requested_check_out: form.requested_check_out ? (form.requested_check_out.length === 5 ? `${form.requested_check_out}:00` : form.requested_check_out) : null,
        requested_break_hours: form.requested_break_hours !== "" && form.requested_break_hours !== null ? parseFloat(form.requested_break_hours) : null,
        reason: form.reason,
      });
    } catch (err) {
      const respData = err?.response?.data;
      const backendErrors = respData?.errors || (respData && typeof respData === "object" && !respData.message && !respData.detail ? respData : null);
      if (backendErrors && typeof backendErrors === "object") {
        const formatted = {};
        for (const key in backendErrors) {
          const val = backendErrors[key];
          formatted[key] = Array.isArray(val) ? val[0] : val;
        }
        setFieldErrors(formatted);
      }
      setError(err?.response?.data?.message || err?.response?.data?.detail || "Please check the field errors below.");
    } finally {
      setSaving(false);
    }
  };

  // Blocked only in the self-service flow (no pre-supplied attendanceRecord)
  // once a date has been chosen but no matching attendance record exists for it.
  const canSubmit = !!activeRecord || (!attendanceRecord && !selectedDate);

  return (
    <Modal
      title="Request Regularization"
      onClose={onClose}
      size="lg"
    >
      {error && <div className="form-error">{error}</div>}

      <div className="attendance-form-body">

        {!attendanceRecord && (
          <div className="form-section-card">
            <div className="section-title-premium"><FaCalendarDay /> Select Attendance Date</div>
            <div>
              <DatePicker
                className={fieldErrors.attendance ? "input-error" : ""}
                value={selectedDate}
                maxDate={new Date().toISOString().split("T")[0]}
                onChange={(val) => {
                  setSelectedDate(val);
                  if (fieldErrors.attendance) setFieldErrors({ ...fieldErrors, attendance: null });
                }}
              />
              {fieldErrors.attendance && <div className="field-error">{fieldErrors.attendance}</div>}
              {selectedDate && !activeRecord && !fieldErrors.attendance && (
                <div style={{ marginTop: "10px", padding: "10px 12px", background: "#fef2f2", color: "#dc2626", borderRadius: "9px", border: "1px solid #fecaca", fontSize: "0.85rem", fontWeight: 500 }}>
                  No attendance record found on <strong>{selectedDate}</strong>. Please choose another date or mark attendance first.
                </div>
              )}
            </div>
          </div>
        )}

        <div className="form-section-card">
          <div className="section-title-premium">
            <FaClock /> Current Record
          </div>

          <div className="info-readout-3">
            <div className="info-item">
              <strong>Date</strong>
              <span>{activeRecord?.date || selectedDate || "—"}</span>
            </div>

            <div className="info-item">
              <strong>Employee</strong>
              <span>{activeRecord?.employee_detail?.full_name || activeRecord?.employee_name || "Self"}</span>
            </div>

            <div className="info-item">
              <strong>Current Break</strong>
              <span>{activeRecord?.total_break_hours ? `${activeRecord.total_break_hours}h` : "0h"}</span>
            </div>

            <div className="info-item">
              <strong>Current Check-In</strong>
              <span>{activeRecord?.check_in_time || "—"}</span>
            </div>

            <div className="info-item">
              <strong>Current Check-Out</strong>
              <span>{activeRecord?.check_out_time || "—"}</span>
            </div>
          </div>
        </div>

        {/* REQUESTED CHANGES */}
        <div className="form-section-card">
          <div className="section-title-premium"><FaClipboardList /> Requested Changes</div>
          <div className="grid-2">
            <div>
              <label>Proposed Check-In Time</label>
              <TimePicker
                className={fieldErrors.requested_check_in ? "input-error" : ""}
                value={form.requested_check_in}
                onChange={(val) => { setForm({ ...form, requested_check_in: val }); if (fieldErrors.requested_check_in) setFieldErrors({ ...fieldErrors, requested_check_in: null }); }}
              />
              {fieldErrors.requested_check_in && <div className="field-error">{fieldErrors.requested_check_in}</div>}
            </div>
            <div>
              <label>Proposed Check-Out Time</label>
              <TimePicker
                className={fieldErrors.requested_check_out ? "input-error" : ""}
                value={form.requested_check_out}
                onChange={(val) => { setForm({ ...form, requested_check_out: val }); if (fieldErrors.requested_check_out) setFieldErrors({ ...fieldErrors, requested_check_out: null }); }}
              />
              {fieldErrors.requested_check_out && <div className="field-error">{fieldErrors.requested_check_out}</div>}
            </div>
            <div>
              <label>Proposed Break Hours (e.g. 1.0)</label>
              <input
                className={fieldErrors.requested_break_hours ? "input-error" : ""}
                type="number"
                step="0.1"
                min="0"
                max="8"
                placeholder="e.g. 1.0"
                value={form.requested_break_hours}
                onChange={(e) => { setForm({ ...form, requested_break_hours: e.target.value }); if (fieldErrors.requested_break_hours) setFieldErrors({ ...fieldErrors, requested_break_hours: null }); }}
              />
              {fieldErrors.requested_break_hours && <div className="field-error">{fieldErrors.requested_break_hours}</div>}
            </div>
          </div>
        </div>

        {/* REASON */}
        <div className="form-section-card">
          <div className="section-title-premium"><FaStickyNote /> Reason</div>
          <div>
            <textarea
              className={fieldErrors.reason ? "input-error" : ""}
              placeholder="Please provide a valid reason for regularization..."
              value={form.reason}
              onChange={(e) => { setForm({ ...form, reason: e.target.value }); if (fieldErrors.reason) setFieldErrors({ ...fieldErrors, reason: null }); }}
            />
            {fieldErrors.reason && <div className="field-error">{fieldErrors.reason}</div>}
          </div>
        </div>

      </div>

      <div className="modal-footer" style={{ marginLeft: -26, marginRight: -26, marginBottom: -22, marginTop: 20 }}>
        <div className="modal-actions">
          <button className="primary" onClick={handleSave} disabled={saving || !canSubmit}>{saving ? "Submitting..." : "Submit Request"}</button>
          <button className="secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </Modal>
  );
};

export default RegularizationModal;